// PR-31: top bar — hamburger / contextual title / offline+queued chips.
// PR-35: optional overflow `⋯` menu for per-tab actions (Distill on Chat,
//        Documents on Cards, etc.). Tapping ⋯ opens a bottom-sheet menu.

import { useEffect, useState } from 'react'

export interface TopBarAction {
  id: string
  label: string
  /** Optional secondary copy under the label (e.g. "and 3 more"). */
  hint?: string
  disabled?: boolean
  destructive?: boolean
  onSelect: () => void
}

interface MobileTopBarProps {
  title: string
  subtitle?: string | null
  onOpenDrawer: () => void

  /** PR-28 offline chips. */
  online?: boolean
  queueCount?: number

  /** PR-35: per-tab actions. When omitted (or empty), no `⋯` button shows. */
  actions?: TopBarAction[]
}

export function MobileTopBar({
  title,
  subtitle,
  onOpenDrawer,
  online = true,
  queueCount = 0,
  actions,
}: MobileTopBarProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const hasActions = !!actions && actions.length > 0

  useEffect(() => {
    if (!menuOpen) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [menuOpen])

  return (
    <>
      <header className="mobile-topbar">
        <button
          type="button"
          className="mobile-topbar__hamburger"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
        >
          <span aria-hidden>☰</span>
        </button>

        <div className="mobile-topbar__titles">
          <div className="mobile-topbar__title">{title}</div>
          {subtitle && (
            <div className="mobile-topbar__subtitle mono">{subtitle}</div>
          )}
        </div>

        {!online && (
          <span
            className="mobile-topbar__offline"
            title={
              queueCount > 0
                ? `${queueCount} capture${queueCount === 1 ? '' : 's'} queued — will sync on reconnect`
                : 'Offline — captures will queue locally'
            }
          >
            <span className="mobile-topbar__offline-dot" aria-hidden>●</span>
            Offline
            {queueCount > 0 && ` · ${queueCount} queued`}
          </span>
        )}
        {online && queueCount > 0 && (
          <span
            className="mobile-topbar__queued"
            title={`${queueCount} capture${queueCount === 1 ? '' : 's'} queued — retrying`}
          >
            ↻ {queueCount}
          </span>
        )}

        {hasActions && (
          <button
            type="button"
            className="mobile-topbar__overflow"
            onClick={() => setMenuOpen(true)}
            aria-label="More actions"
          >
            <span aria-hidden>⋯</span>
          </button>
        )}
      </header>

      {menuOpen && hasActions && (
        <div
          className="mobile-action-sheet-backdrop"
          onClick={() => setMenuOpen(false)}
        >
          <div
            className="mobile-action-sheet"
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            {actions!.map((a) => (
              <button
                key={a.id}
                type="button"
                className={
                  'mobile-action-sheet__row' +
                  (a.destructive ? ' mobile-action-sheet__row--danger' : '')
                }
                disabled={a.disabled}
                onClick={() => {
                  setMenuOpen(false)
                  a.onSelect()
                }}
              >
                <span>{a.label}</span>
                {a.hint && (
                  <span className="mobile-topbar__action-hint mono">
                    {a.hint}
                  </span>
                )}
              </button>
            ))}
            <button
              type="button"
              className="mobile-action-sheet__row mobile-action-sheet__row--cancel"
              onClick={() => setMenuOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  )
}
